import { useState, useEffect } from 'react';
import Plot from 'react-plotly.js';
import { TickerIcon, PortfolioIcon, ScreenerIcon } from '../components/Icons';
import {
  fetchPortfolio, fetchWatchlist, fetchQuote, fetchSparkline,
  fetchHistory, addToWatchlist, removeFromWatchlist, formatMcap, detectCurrency
} from '../utils/api';

const TIMEFRAMES = ['1D', '5D', '1M', '6M', 'YTD', '1Y', '5Y', 'All'];
const TF_MAP = {
  '1D': ['1d', '1m'], '5D': ['5d', '5m'], '1M': ['1mo', '30m'],
  '6M': ['6mo', '1d'], 'YTD': ['ytd', '1d'], '1Y': ['1y', '1d'],
  '5Y': ['5y', '1wk'], 'All': ['max', '1mo'],
};

function SparklineSVG({ prices }) {
  if (!prices || prices.length < 2) return null;
  const width = 80, height = 28;
  const mn = Math.min(...prices), mx = Math.max(...prices);
  const rng = mx - mn || 1;
  const openP = prices[0];

  const toXY = (i, p) => ({
    x: (i / (prices.length - 1) * width).toFixed(1),
    y: (height - (p - mn) / rng * (height - 2) - 1).toFixed(1),
  });

  const segments = { green: [], red: [] };
  let curSeg = [], curColor = prices[0] >= openP ? 'green' : 'red';
  curSeg.push(toXY(0, prices[0]));

  for (let i = 1; i < prices.length; i++) {
    const p = prices[i], prevP = prices[i - 1];
    const above = p >= openP, prevAbove = prevP >= openP;
    if (above !== prevAbove && prevP !== openP && p !== openP) {
      const frac = (openP - prevP) / (p - prevP);
      const crossI = (i - 1) + frac;
      const cross = toXY(crossI, openP);
      curSeg.push(cross);
      segments[curColor].push([...curSeg]);
      curSeg = [cross];
      curColor = above ? 'green' : 'red';
    }
    curSeg.push(toXY(i, p));
  }
  if (curSeg.length) segments[curColor].push(curSeg);

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      {segments.green.map((seg, i) => (
        <polyline key={`g${i}`} points={seg.map(p => `${p.x},${p.y}`).join(' ')}
          fill="none" stroke="#00ff9d" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      ))}
      {segments.red.map((seg, i) => (
        <polyline key={`r${i}`} points={seg.map(p => `${p.x},${p.y}`).join(' ')}
          fill="none" stroke="#ff4d4d" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      ))}
    </svg>
  );
}

function StockRow({ quote, sparkline, onClick }) {
  if (!quote) return null;
  const isPos = quote.change >= 0;
  const clr = isPos ? '#00ff9d' : '#ff4d4d';
  const sign = isPos ? '+' : '';
  const cur = quote.currency === 'CAD' ? 'C$' : '$';

  return (
    <div className="ticker-row" onClick={onClick}>
      <span className="ticker-col-symbol">{quote.symbol}</span>
      <span className="ticker-col-name">{quote.name}</span>
      <span className="ticker-col-mcap">{formatMcap(quote.market_cap)}</span>
      <span className="ticker-col-spark"><SparklineSVG prices={sparkline} /></span>
      <span className="ticker-col-price">
        {cur}{quote.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
      <span className="ticker-col-change" style={{ color: clr }}>
        {sign}{quote.pct.toFixed(2)}%
      </span>
    </div>
  );
}

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

function TickerPane({ symbol, paneIdx, compact, allOptions, onChangeSymbol }) {
  const [tf, setTf] = useState('1D');
  const [quote, setQuote] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!symbol) return;
    setLoading(true);
    Promise.all([
      fetchQuote(symbol),
      fetchHistory(symbol, TF_MAP[tf][0], TF_MAP[tf][1]),
    ]).then(([q, h]) => {
      setQuote(q);
      setHistory(h);
      setLoading(false);
    });
  }, [symbol, tf]);

  if (!symbol) return null;

  const curSym = quote && quote.currency === 'CAD' ? 'C$' : '$';

  return (
    <div>
      {loading ? (
        <div className="loading-text"><span className="spinner" />Loading...</div>
      ) : quote ? (
        <>
          <div className="glass-card ticker-price-card" style={{ padding: compact ? '16px 18px 12px' : '24px 28px 20px' }}>
            <div className="ticker-card-top">
              <div className="ticker-card-info">
                <div className="ticker-card-name">
                  <span style={{ fontSize: compact ? '1rem' : '1.25rem', fontWeight: 700, color: '#f1f5f9' }}>{quote.name}</span>
                  <span style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 500 }}>({quote.symbol})</span>
                </div>
              </div>
              <div className="ticker-card-selectors">
                <select className="input-field" value={symbol} onChange={e => onChangeSymbol(e.target.value)}
                  style={{ width: 90, padding: '6px 8px', fontSize: '0.8rem' }}>
                  {allOptions.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
                <select className="input-field" value={tf} onChange={e => setTf(e.target.value)}
                  style={{ width: 52, padding: '6px 6px', fontSize: '0.8rem' }}>
                  {TIMEFRAMES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="ticker-card-price-row">
              <span className="ticker-card-price" style={{
                fontSize: compact ? '1.8rem' : '2.6rem',
                color: quote.change >= 0 ? '#00ff9d' : '#ff4d4d'
              }}>
                {curSym}{quote.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              <span className="ticker-card-change" style={{
                fontSize: compact ? '0.85rem' : '1rem',
                color: quote.change >= 0 ? '#00ff9d' : '#ff4d4d'
              }}>
                {quote.change >= 0 ? '▲' : '▼'} {quote.change >= 0 ? '+' : ''}{quote.change.toFixed(2)} ({quote.change >= 0 ? '+' : ''}{quote.pct.toFixed(2)}%)
              </span>
            </div>
            <div style={{ marginTop: 2, color: '#64748b', fontSize: compact ? '0.72rem' : '0.8rem' }}>
              {quote.market_state === 'REGULAR' || quote.market_state === 'PRE' ? 'Market Open' : 'Market Closed'}
            </div>
          </div>

          {history && history.length > 0 ? (
            <Plot
              data={(() => {
                const dates = history.map(h => h.date);
                const closes = history.map(h => h.close);
                const openPrice = quote.prev_close;
                const greenY = closes.map(c => c >= openPrice ? c : openPrice);
                const redY = closes.map(c => c <= openPrice ? c : openPrice);
                return [
                  { x: dates, y: Array(dates.length).fill(openPrice), mode: 'lines', line: { width: 0 }, showlegend: false, hoverinfo: 'skip' },
                  { x: dates, y: greenY, mode: 'lines', line: { color: '#00ff9d', width: 2 }, fill: 'tonexty', fillcolor: 'rgba(0,255,157,0.06)', showlegend: false, hoverinfo: 'skip' },
                  { x: dates, y: Array(dates.length).fill(openPrice), mode: 'lines', line: { width: 0 }, showlegend: false, hoverinfo: 'skip' },
                  { x: dates, y: redY, mode: 'lines', line: { color: '#ff4d4d', width: 2 }, fill: 'tonexty', fillcolor: 'rgba(255,77,77,0.06)', showlegend: false, hoverinfo: 'skip' },
                  { x: dates, y: closes, mode: 'lines', line: { color: 'rgba(0,0,0,0)', width: 0 }, name: 'Price', showlegend: false,
                    hovertemplate: '<b>%{x}</b><br>Price: ' + curSym + '%{y:,.2f}<extra></extra>' },
                ];
              })()}
              layout={{
                paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)',
                font: { color: '#94a3b8', family: 'Inter, sans-serif' },
                margin: { t: 8, b: 30, l: compact ? 40 : 60, r: 12 },
                height: isMobile ? (compact ? 130 : 240) : (compact ? 260 : 480),
                showlegend: false, hovermode: 'x unified',
                xaxis: { showgrid: true, gridcolor: 'rgba(148,163,184,0.06)', showline: false,
                  tickfont: { size: compact ? 10 : 11, color: '#64748b' } },
                yaxis: { showgrid: true, gridcolor: 'rgba(148,163,184,0.06)', showline: false,
                  tickfont: { size: compact ? 10 : 11, color: '#64748b' }, tickprefix: curSym },
                shapes: [
                  { type: 'line', y0: quote.prev_close, y1: quote.prev_close, x0: 0, x1: 1, xref: 'paper',
                    line: { dash: 'dot', color: 'rgba(148,163,184,0.3)', width: 1 } },
                ],
              }}
              config={{ displayModeBar: false }}
              style={{ width: '100%' }}
            />
          ) : (
            <div className="alert-box alert-warning">No chart data available for this timeframe.</div>
          )}

          {!compact && (
            <div className="ticker-metrics-row">
              <div className="ticker-metric-card">
                <div className="ticker-metric-label">Previous Close</div>
                <div className="ticker-metric-value">
                  {curSym}{quote.prev_close.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div className="ticker-metric-card">
                <div className="ticker-metric-label">Market Cap</div>
                <div className="ticker-metric-value">{formatMcap(quote.market_cap)}</div>
              </div>
              <div className="ticker-metric-card">
                <div className="ticker-metric-label">Change</div>
                <div className="ticker-metric-value" style={{ color: quote.change >= 0 ? '#00ff9d' : '#ff4d4d' }}>
                  {quote.change >= 0 ? '+' : ''}{quote.change.toFixed(2)} ({quote.change >= 0 ? '+' : ''}{quote.pct.toFixed(2)}%)
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="alert-box alert-error">Could not fetch data for {symbol}.</div>
      )}
    </div>
  );
}

function LiveTicker() {
  const [portfolio, setPortfolio] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [holdingData, setHoldingData] = useState([]);
  const [watchlistData, setWatchlistData] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [viewSymbols, setViewSymbols] = useState([]);
  const [viewMode, setViewMode] = useState(1);
  const [editing, setEditing] = useState(false);
  const [newWatchSym, setNewWatchSym] = useState('');

  useEffect(() => { loadLists(); }, []);

  async function loadLists() {
    setLoadingList(true);
    const [p, wl] = await Promise.all([fetchPortfolio(), fetchWatchlist()]);
    setPortfolio(p);
    setWatchlist(wl);

    const holdTickers = p.map(h => h.ticker);
    const wlDisplay = wl.filter(w => !holdTickers.map(t => t.toUpperCase()).includes(w.toUpperCase()));

    const hData = [];
    for (const tkr of holdTickers) {
      const [q, s] = await Promise.all([fetchQuote(tkr), fetchSparkline(tkr)]);
      hData.push({ quote: q, sparkline: s });
    }
    setHoldingData(hData);

    const wData = [];
    for (const tkr of wlDisplay) {
      const [q, s] = await Promise.all([fetchQuote(tkr), fetchSparkline(tkr)]);
      wData.push({ quote: q, sparkline: s, symbol: tkr });
    }
    setWatchlistData(wData);
    setLoadingList(false);
  }

  function openTicker(sym) {
    const mode = viewMode;
    const syms = [...viewSymbols];
    if (syms.includes(sym)) return;
    if (syms.length < mode) {
      syms.push(sym);
    } else {
      syms.splice(0, syms.length, sym);
    }
    setViewSymbols(syms);
  }

  function getAllOptions() {
    const hold = portfolio.map(h => h.ticker.toUpperCase());
    const wl = watchlist.map(w => w.toUpperCase());
    const seen = new Set();
    const opts = [];
    [...hold, ...wl].forEach(s => { if (!seen.has(s)) { seen.add(s); opts.push(s); } });
    return opts;
  }

  async function handleAddWatchlist() {
    if (!newWatchSym.trim()) return;
    const s = newWatchSym.trim().toUpperCase();
    await addToWatchlist(s);
    setNewWatchSym('');
    await loadLists();
  }

  async function handleRemoveWatchlist(sym) {
    await removeFromWatchlist(sym);
    await loadLists();
  }

  const holdTickers = portfolio.map(h => h.ticker);
  const wlDisplay = watchlist.filter(w => !holdTickers.map(t => t.toUpperCase()).includes(w.toUpperCase()));

  if (viewSymbols.length > 0) {
    const allOpts = getAllOptions();
    const syms = [...viewSymbols];
    while (syms.length < viewMode) {
      const next = allOpts.find(o => !syms.includes(o)) || allOpts[0];
      if (next) syms.push(next);
    }

    return (
      <div>
        <div className="page-title">
          <TickerIcon size={28} color="#00ff9d" />
          <h1>Live Ticker</h1>
        </div>
        <p className="hero-subtitle">Multi-pane view</p>

        <div className="toolbar">
          <button className="btn-secondary" onClick={() => setViewSymbols([])}>Back to Lists</button>
          <div className="toolbar-spacer" />
          <select className="input-field" value={viewMode} onChange={e => {
            const m = parseInt(e.target.value);
            setViewMode(m);
            const s = [...syms];
            while (s.length < m) {
              const next = allOpts.find(o => !s.includes(o)) || allOpts[0];
              if (next) s.push(next);
            }
            setViewSymbols(s.slice(0, m));
          }} style={{ width: 140 }}>
            <option value={1}>1 Ticker</option>
            <option value={2}>2 Tickers</option>
            <option value={4}>4 Tickers</option>
          </select>
        </div>

        {viewMode === 1 ? (
          <TickerPane symbol={syms[0]} paneIdx={0} compact={false} allOptions={allOpts}
            onChangeSymbol={s => { const ns = [...syms]; ns[0] = s; setViewSymbols(ns.slice(0, viewMode)); }} />
        ) : (
          <div className={viewMode === 2 ? 'pane-grid-2' : 'pane-grid-4'}>
            {syms.slice(0, viewMode).map((sym, i) => (
              <TickerPane key={i} symbol={sym} paneIdx={i} compact={true} allOptions={allOpts}
                onChangeSymbol={s => { const ns = [...syms]; ns[i] = s; setViewSymbols(ns.slice(0, viewMode)); }} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="page-title">
        <TickerIcon size={28} color="#00ff9d" />
        <h1>Live Ticker</h1>
      </div>
      <p className="hero-subtitle">Your holdings & watchlist — click any stock to view details</p>

      {loadingList ? (
        <div className="loading-text"><span className="spinner" />Loading holdings and watchlist...</div>
      ) : (
        <>
          {holdTickers.length > 0 && (
            <div className="glass-card" style={{ padding: '18px 0 8px', marginBottom: 20 }}>
              <div className="section-header" style={{ padding: '0 16px 10px' }}>
                <PortfolioIcon size={18} color="#94a3b8" />
                <span className="section-title">Your Holdings</span>
                <span className="section-count">{holdTickers.length} stocks</span>
              </div>
              <div className="ticker-table-head">
                <span className="ticker-col-symbol">Symbol</span>
                <span className="ticker-col-name">Name</span>
                <span className="ticker-col-mcap">Mkt Cap</span>
                <span className="ticker-col-spark">1D Chart</span>
                <span className="ticker-col-price">Price</span>
                <span className="ticker-col-change">Change</span>
              </div>
              {holdingData.map((d, i) => (
                <StockRow key={i} quote={d.quote} sparkline={d.sparkline}
                  onClick={() => d.quote && openTicker(d.quote.symbol)} />
              ))}
            </div>
          )}

          <div className="glass-card" style={{ padding: '18px 0 8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px 10px' }}>
              <div className="section-header" style={{ padding: 0 }}>
                <ScreenerIcon size={18} color="#94a3b8" />
                <span className="section-title">Watchlist</span>
                <span className="section-count">{wlDisplay.length} stocks</span>
              </div>
              {wlDisplay.length > 0 && (
                <button className={editing ? 'btn-primary' : 'btn-secondary'} style={{ padding: '6px 16px', fontSize: '0.82rem' }}
                  onClick={() => setEditing(!editing)}>
                  {editing ? 'Done' : 'Edit Watchlist'}
                </button>
              )}
            </div>
            <div className="ticker-table-head">
              <span className="ticker-col-symbol">Symbol</span>
              <span className="ticker-col-name">Name</span>
              <span className="ticker-col-mcap">Mkt Cap</span>
              <span className="ticker-col-spark">1D Chart</span>
              <span className="ticker-col-price">Price</span>
              <span className="ticker-col-change">Change</span>
              {editing && <span style={{ flex: '0 0 50px' }} />}
            </div>
            {wlDisplay.length === 0 ? (
              <p style={{ color: '#475569', textAlign: 'center', padding: 20, fontSize: '0.9rem' }}>
                Your watchlist is empty. Add stocks below.
              </p>
            ) : (
              watchlistData.map((d, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <StockRow quote={d.quote} sparkline={d.sparkline}
                      onClick={() => d.quote && openTicker(d.quote.symbol)} />
                  </div>
                  {editing && (
                    <button className="btn-danger" style={{ marginRight: 12 }}
                      onClick={() => handleRemoveWatchlist(d.symbol)}>✕</button>
                  )}
                </div>
              ))
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <input className="input-field" value={newWatchSym} onChange={e => setNewWatchSym(e.target.value)}
              placeholder="e.g. TSLA, SHOP.TO, NVDA" style={{ flex: 3 }}
              onKeyDown={e => e.key === 'Enter' && handleAddWatchlist()} />
            <button className="btn-primary" onClick={handleAddWatchlist}>Add</button>
          </div>
        </>
      )}
    </div>
  );
}

export default LiveTicker;
